import { CashFlow, CashFlowType, ExpenseGroup, NewCashFlow, PurchaseUnitMode } from '@api/CashFlows/CashFlow';
import { ExpenseCategory } from '@api/ExpenseCategories/ExpenseCategory';
import { Input } from '@api/Inputs/Input';
import { formatQuantity } from '@api/BranchOfficeInventories/BranchOfficeInventory';
import { currencyFormatter } from '@utils/Numbers';
import {
    can,
    Capability,
    ExpenseListScope,
    getCapabilityRule,
    PAYROLL_PROFILES,
    PermissionSubject
} from '@utils/Permissions/Capabilities';
import { isUserActive, UserType } from '@api/Users/User';
import { RecurringAmountMode, RecurringExpense } from '@api/RecurringExpenses/RecurringExpense';
import {
    countsInCashCount,
    countsInIncomeStatement,
    expenseCategoryKeyOf,
    expenseGroupOf,
    isActiveFlow,
    isExpenseFlow,
    UNCATEGORIZED
} from '@utils/CashFlows';
import { toDateSafe, toMillisSafe } from '@utils/Date';

// ============================================================================
// REGISTRAR UN GASTO — las reglas, puras y con pruebas (plan de Gastos §5.1, F2).
//
// La pantalla pregunta aquí qué categorías mostrar y si el gasto se puede guardar;
// `ExpenseServ.saveExpense` vuelve a preguntar antes de escribir, porque **un gate
// va sobre la función que ejecuta, no sobre los botones**.
// ============================================================================

/** Lo que captura la pantalla. */
export interface ExpenseDraft {
    category?: ExpenseCategory;
    amount: number;
    /** Descripción libre. Obligatoria salvo que la categoría ya diga qué fue (nómina, insumo). */
    note: string;
    /** ¿Salió de la caja de ESTE TURNO? Solo lo que sí resta en el corte. */
    paidFromCash: boolean;
    payrollUser?: { uid: string; name: string };
    input?: { id: string; name: string };
    /** Compra en la calle (plan de inventario F1): cuánto se compró, en `purchaseUnitMode`. */
    purchaseQuantity?: number;
    /** Presentaciones del catálogo (default) o unidad base. */
    purchaseUnitMode?: PurchaseUnitMode;
    /** La unidad con la que se capturó, para el texto del gasto ("pza", "rebanadas"). */
    purchaseUnitLabel?: string;
    /** `yyyy-MM`: de qué mes es un gasto fijo o variable (la luz de septiembre se paga en octubre). */
    period?: string;
}

// ---------------------------------------------------------------------------
// La compra en la calle entra al inventario (plan de inventario F1). Aquí vive
// solo lo que la PANTALLA necesita decir: la conversión en vivo y el precio
// unitario contra el catálogo. La conversión que manda es la del server
// (`InventoryPurchase.ts`): ésta solo informa.
// ---------------------------------------------------------------------------

/** Lo que se necesita del insumo del catálogo para informar (el `Input` completo del snapshot). */
export type PurchaseInputInfo = Pick<Input, 'presentation' | 'unit' | 'costOfSale' | 'unitCostSale'>;

/** "pza" (presentación) o "rebanadas" (unidad base): la etiqueta de cada modo. */
export const purchaseUnitLabelOf = (mode: PurchaseUnitMode, input?: PurchaseInputInfo): string =>
    (mode === 'base' ? input?.unit?.unit : input?.presentation?.unit) || '';

/** ¿Vale la pena ofrecer el modo "unidad base"? Solo si la presentación rinde más de una. */
export const purchaseHasBaseMode = (input?: PurchaseInputInfo): boolean => Number(input?.unit?.value) > 1;

/** Las unidades base que entran (informativo; el server lo recalcula con la misma regla). */
export const purchaseBaseUnits = (quantity: number, mode: PurchaseUnitMode, input?: PurchaseInputInfo): number => {
    const qty = Number(quantity) || 0;
    if (mode === 'base') return qty;
    const factor = Number(input?.unit?.value);
    return qty * (Number.isFinite(factor) && factor > 0 ? factor : 1);
};

/** "3 pza = 138 rebanadas" mientras se captura; vacío sin cantidad o cuando no hay nada que convertir. */
export const describePurchaseConversion = (quantity: number, mode: PurchaseUnitMode, input?: PurchaseInputInfo): string => {
    const qty = Number(quantity) || 0;
    if (qty <= 0 || !input) return '';
    const base = `${formatQuantity(purchaseBaseUnits(qty, mode, input))} ${input.unit?.unit || ''}`.trim();
    if (mode === 'base' || !purchaseHasBaseMode(input)) return base;
    return `${formatQuantity(qty)} ${input.presentation?.unit || ''} = ${base}`.trim();
};

/**
 * "Sale a $150.00 por pza; el CEDIS la surte a $450.00": una diferencia grande casi
 * siempre es una cantidad o una unidad mal capturada. **Informa, no bloquea.** Vacío
 * sin cantidad, sin monto o sin costo de referencia en el catálogo.
 */
export const describePurchasePrice = (
    amount: number,
    quantity: number,
    mode: PurchaseUnitMode,
    input?: PurchaseInputInfo
): string => {
    const qty = Number(quantity) || 0;
    const total = Number(amount) || 0;
    if (qty <= 0 || total <= 0 || !input) return '';
    const reference = mode === 'base' ? Number(input.unitCostSale) : Number(input.costOfSale);
    if (!(reference > 0)) return '';
    const label = purchaseUnitLabelOf(mode, input);
    return `Sale a ${currencyFormatter(total / qty)} por ${label}; el CEDIS la surte a ${currencyFormatter(reference)}`;
};

/** "3 pza": la cantidad como se escribe en el gasto, con su unidad. */
export const describePurchaseQuantity = (flow: Pick<CashFlow, 'purchaseQuantity' | 'purchaseUnitLabel'>): string => {
    const qty = Number(flow.purchaseQuantity);
    if (!(qty > 0)) return '';
    return `${formatQuantity(qty)} ${flow.purchaseUnitLabel || ''}`.trim();
};

export interface ExpenseContext {
    subject: PermissionSubject;
    branchOfficeId?: string;
    /** Caja abierta de hoy en la sucursal (`isActiveCashRegister`). */
    hasOpenCashRegister: boolean;
}

/** Fijos y variables son del MES: llevan `period`. */
export const isMonthlyGroup = (group?: ExpenseGroup): boolean =>
    group === ExpenseGroup.Fixed || group === ExpenseGroup.Variable;

/** El permiso que pide capturar una categoría (tabla §4.1). */
export const captureCapabilityOf = (category: ExpenseCategory): Capability => {
    if (category.askCollaborator || category.group === ExpenseGroup.Payroll) return Capability.ExpenseCapturePayroll;
    if (category.group === ExpenseGroup.Fixed) return Capability.ExpenseCaptureFixed;
    return Capability.ExpenseCaptureOperating;
};

/**
 * Las que este rol puede capturar en esta sucursal, activas. Las que no puede **no
 * aparecen** (no se muestran deshabilitadas, §5.1).
 */
export const getCapturableCategories = (
    categories: ExpenseCategory[],
    subject: PermissionSubject,
    branchOfficeId?: string
): ExpenseCategory[] =>
    categories.filter(
        (category) => category.active && can(subject, captureCapabilityOf(category), { branchOfficeId })
    );

/**
 * A quién se le puede pagar nómina en la sucursal: activo, con perfil de sucursal
 * y asignado A ELLA (no basta `['all']`, que son los admins generales). Por nombre.
 */
export const getPayrollTeam = (users: UserType[], branchOfficeId: string): { uid: string; name: string }[] =>
    users
        .filter(
            (user) =>
                isUserActive(user) &&
                PAYROLL_PROFILES.includes(user.profile?.profile) &&
                (user.profile?.branchOffices || []).includes(branchOfficeId)
        )
        .map((user) => ({ uid: user.uid, name: user.profile.name }))
        .sort((a, b) => a.name.localeCompare(b.name));

/** ¿La descripción es obligatoria? No cuando la categoría ya dice qué fue. */
export const isNoteRequired = (draft: Pick<ExpenseDraft, 'category'>): boolean =>
    !draft.category?.askCollaborator && !draft.category?.askInput;

/** El primer problema que impide guardar, dicho para quien captura; `null` = se puede. */
export const validateExpense = (draft: ExpenseDraft, context: ExpenseContext): string | null => {
    const { category } = draft;
    if (!category) return 'Elige la categoría del gasto';
    if (!category.active) return `La categoría "${category.name}" ya no está activa`;
    if (!can(context.subject, captureCapabilityOf(category), { branchOfficeId: context.branchOfficeId })) {
        return `Tu perfil no puede registrar gastos de "${category.name}"`;
    }
    if (!(Number(draft.amount) > 0)) return 'El monto tiene que ser mayor a cero';
    if (category.askCollaborator && !draft.payrollUser?.uid) return 'Elige a quién se le paga';
    if (category.askInput && !draft.input?.id) return 'Elige el insumo de la lista';
    // Sin cantidad la compra no entra al inventario, que es a lo que viene (F1)
    if (category.askInput && !(Number(draft.purchaseQuantity) > 0)) return '¿Cuánto compraste? Escribe la cantidad';
    if (isNoteRequired(draft) && !draft.note.trim()) return 'Escribe una descripción';
    if (isMonthlyGroup(category.group) && !/^\d{4}-\d{2}$/.test(draft.period || '')) return 'Elige de qué mes es';
    if (draft.paidFromCash && !context.hasOpenCashRegister) {
        return 'Para sacar dinero de la caja tiene que haber una caja abierta';
    }
    return null;
};

/**
 * La descripción que leen los lectores de siempre (modal "Otros gastos", Excel,
 * bitácora): nunca vacía, aunque la categoría no la haya pedido.
 */
export const describeExpense = (draft: ExpenseDraft): string => {
    const note = draft.note.trim();
    const quantity = describePurchaseQuantity(draft);
    const head = draft.category?.askCollaborator
        ? `Nómina: ${draft.payrollUser?.name || ''}`.trim()
        : draft.category?.askInput
        ? [draft.input?.name || '', quantity].filter(Boolean).join(' — ')
        : '';
    if (head && note) return `${head} — ${note}`;
    return head || note;
};

/**
 * El movimiento a guardar. **Sin campos `undefined`** (no es un valor de
 * Firestore): lo opcional solo va si existe. Quién, dónde y cuándo los pone
 * `saveCashFlow`.
 */
export const buildExpenseCashFlow = (draft: ExpenseDraft): NewCashFlow => {
    const category = draft.category!;
    const note = draft.note.trim();
    return {
        // "Envío" sigue siendo `Shipping`: el informe tiene su renglón de envíos.
        type: category.recordsAsShipping ? CashFlowType.Shipping : CashFlowType.Output,
        amount: Math.round(Number(draft.amount) * 100) / 100,
        description: describeExpense(draft),
        categoryId: category.id,
        categoryName: category.name,
        categoryGroup: category.group,
        paidFromCash: draft.paidFromCash,
        ...(category.askCollaborator && draft.payrollUser
            ? { payrollUserId: draft.payrollUser.uid, payrollUserName: draft.payrollUser.name }
            : {}),
        ...(category.askInput && draft.input ? { inputId: draft.input.id, inputName: draft.input.name } : {}),
        // La compra entra al inventario (F1): el server la iguala al ledger y borra el pendiente
        ...(category.askInput && draft.input && Number(draft.purchaseQuantity) > 0
            ? {
                  purchaseQuantity: Math.round(Number(draft.purchaseQuantity) * 100) / 100,
                  purchaseUnitMode: draft.purchaseUnitMode === 'base' ? 'base' : 'presentation',
                  ...(draft.purchaseUnitLabel ? { purchaseUnitLabel: draft.purchaseUnitLabel } : {}),
                  inventorySyncPending: true
              }
            : {}),
        ...(isMonthlyGroup(category.group) && draft.period ? { period: draft.period } : {}),
        ...(note ? { note } : {})
    };
};

/** Cómo arranca el interruptor "Salió de la caja de este turno" al elegir la categoría. */
export const defaultPaidFromCash = (category?: ExpenseCategory): boolean => category?.paidFromCashByDefault !== false;

/**
 * Cómo se dice el interruptor. 🚨 **"De este turno" es la palabra que importa**: el
 * letrero decía "Salió del efectivo de la caja" y quien pagaba la renta EN EFECTIVO (con
 * dinero guardado de otros días) lo encendía, porque para él "la caja" es el dinero del
 * negocio. Para el corte es solo el cajón del turno: lo que no salió de ahí, no resta.
 */
export const PAID_FROM_CASH_LABELS = {
    fromCash: 'Salió de la caja de este turno (resta en el corte de hoy)',
    otherMoney: 'Se pagó con otro dinero (no resta en el corte)',
    hint: '¿Otro dinero? Transferencia, efectivo guardado de otros días o dinero del dueño.'
};

/**
 * Desde cuánto un gasto "de la caja" se confirma aunque sea de lo del día. Sale de los
 * datos (30-sep → 8-oct, 115 gastos): las compras del día que sí salen de la caja van de
 * $50 a ~$1,600 (el queso gouda de Centro Histórico, ~$1,100 casi diario); lo que pasó de
 * $2,000 fue nómina, renta o un pago al dueño que esa caja no tenía.
 *
 * ⚠️ **Umbral FIJO a propósito**: compararlo contra lo que "debería haber" en la caja le
 * revelaría a quien captura el esperado del corte ciego (bastaría probar montos).
 */
export const LARGE_CASH_EXPENSE = 2000;

/**
 * ¿Hay que preguntar "¿de verdad salió de la caja de este turno?" antes de guardar?
 * Nómina, fijos y variables siempre (se pagan con otro dinero casi siempre: el 1-oct y el
 * 5-oct dejaron un corte en -$14,903 y $20 k fuera de toda caja), y cualquier otro desde
 * `LARGE_CASH_EXPENSE`. **Pregunta, nunca bloquea**: si de verdad salió de la caja, se
 * registra así.
 */
export const needsCashConfirmation = (draft: Pick<ExpenseDraft, 'category' | 'amount' | 'paidFromCash'>): boolean => {
    if (!draft.paidFromCash || !draft.category) return false;
    const { group, askCollaborator } = draft.category;
    if (askCollaborator || group === ExpenseGroup.Payroll || isMonthlyGroup(group)) return true;
    return Number(draft.amount) >= LARGE_CASH_EXPENSE;
};

/** Ventana del aviso "a esta persona ya se le pagó": la nómina más corta que se ve es semanal. */
export const PAYROLL_REPEAT_DAYS = 6;

/**
 * La nómina más reciente de ESA persona en los últimos `PAYROLL_REPEAT_DAYS` días, si hay:
 * la pantalla avisa antes de registrar otra (el 1-oct y el 4-oct se le registraron $4,200
 * a la misma persona en Esteban de Antuñano). Solo avisa: puede ser un pago real.
 * Las canceladas y las "por confirmar" no cuentan.
 */
export const findRecentPayroll = (
    flows: CashFlow[],
    payrollUserId: string | undefined,
    now: Date = new Date()
): CashFlow | undefined => {
    if (!payrollUserId) return undefined;
    const since = now.getTime() - PAYROLL_REPEAT_DAYS * 86_400_000;
    return flows
        .filter((flow) => flow.payrollUserId === payrollUserId && isActiveFlow(flow) && !flow.pendingConfirmation)
        .filter((flow) => {
            const millis = toMillisSafe(flow.date);
            return millis >= since && millis <= now.getTime();
        })
        .sort((a, b) => toMillisSafe(b.date) - toMillisSafe(a.date))[0];
};

const MONTHS = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre'
];

/** `yyyy-MM` del mes de `now` desplazado `offset` meses (hora local). */
export const periodOf = (now: Date, offset = 0): string => {
    const date = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

/** `2026-09` → `septiembre 2026`. */
export const formatPeriod = (period: string): string => {
    const [year, month] = period.split('-').map(Number);
    return MONTHS[month - 1] ? `${MONTHS[month - 1]} ${year}` : period;
};

/** Los meses que se pueden elegir: este y los dos anteriores (el recibo llega tarde). */
export const selectablePeriods = (now: Date): string[] => [periodOf(now), periodOf(now, -1), periodOf(now, -2)];


// ============================================================================
// LA LISTA DE GASTOS — pestaña Gastos del Informe (plan de Gastos §5.2, F3).
// Lee lo mismo que ya trae el Informe (`cash-flows` del periodo): cero lecturas
// extra. Los filtros van en el cliente, sobre la consulta de siempre (sin índices).
// ============================================================================

export type ExpenseStatus = 'active' | 'pending' | 'cancelled';

/** Cancelado, estimado por confirmar (fijo generado por el aseo) o activo. */
export const expenseStatusOf = (flow: CashFlow): ExpenseStatus => {
    if (!isActiveFlow(flow)) return 'cancelled';
    if (flow.pendingConfirmation) return 'pending';
    return 'active';
};

export const EXPENSE_STATUS_LABELS: Record<ExpenseStatus, string> = {
    active: 'Activo',
    pending: 'Por confirmar',
    cancelled: 'Cancelado'
};

export interface ExpenseListFilters {
    /** `categoryId`, `'operating-shipping'` (incluye el envío histórico), `'uncategorized'` o `'all'`. */
    categoryKey: string;
    /** Un grupo, `'uncategorized'` o `'all'`. */
    group: string;
    /** Quién lo capturó (`userId`) o `'all'`. */
    userId: string;
    status: ExpenseStatus | 'all';
}

export const EMPTY_EXPENSE_FILTERS: ExpenseListFilters = { categoryKey: 'all', group: 'all', userId: 'all', status: 'all' };

const sameLocalDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * Los gastos que se ven, del más nuevo al más viejo. `scope` es lo que el rol puede
 * ver (`getExpenseListScope`): el colaborador, solo lo que él capturó hoy.
 */
export const filterExpenses = (
    flows: CashFlow[],
    filters: ExpenseListFilters,
    scope?: Pick<ExpenseListScope, 'onlyOwn' | 'onlyToday'> & { uid?: string; now?: Date }
): CashFlow[] => {
    const now = scope?.now || new Date();
    return flows
        .filter(isExpenseFlow)
        .filter((flow) => {
            if (scope?.onlyOwn && flow.userId !== scope.uid) return false;
            if (scope?.onlyToday) {
                const date = toDateSafe(flow.date);
                if (!date || !sameLocalDay(date, now)) return false;
            }
            if (filters.categoryKey !== 'all' && expenseCategoryKeyOf(flow) !== filters.categoryKey) return false;
            if (filters.group !== 'all' && (expenseGroupOf(flow) || UNCATEGORIZED) !== filters.group) return false;
            if (filters.userId !== 'all' && flow.userId !== filters.userId) return false;
            if (filters.status !== 'all' && expenseStatusOf(flow) !== filters.status) return false;
            return true;
        })
        .sort((a, b) => toMillisSafe(b.date) - toMillisSafe(a.date));
};

export interface ExpenseSummary {
    /** Todo lo activo, estimados incluidos (propinas entregadas y ajustes también). */
    total: number;
    /** Por grupo; `uncategorized` = lo capturado antes de F2. */
    byGroup: Record<string, number>;
    /** Lo que es gasto del negocio (entra al Estado de Resultados). */
    incomeStatement: number;
    /** Propinas entregadas y ajustes de caja: salen de la caja y no son gasto. */
    outsideStatement: number;
    /** Lo que restó en el corte. */
    fromCash: number;
    /** Fijos generados por el aseo sin confirmar (ya sumados en `total`). */
    estimated: number;
    count: number;
    cancelledCount: number;
}

const cents = (value: number): number => Math.round(value * 100) / 100;

/** Los KPIs de la lista. **Un cancelado se ve (tachado) pero no suma.** */
export const summarizeExpenses = (flows: CashFlow[]): ExpenseSummary => {
    const summary: ExpenseSummary = {
        total: 0,
        byGroup: {},
        incomeStatement: 0,
        outsideStatement: 0,
        fromCash: 0,
        estimated: 0,
        count: 0,
        cancelledCount: 0
    };
    flows.filter(isExpenseFlow).forEach((flow) => {
        if (!isActiveFlow(flow)) {
            summary.cancelledCount += 1;
            return;
        }
        const amount = Number(flow.amount) || 0;
        const group = expenseGroupOf(flow) || UNCATEGORIZED;
        summary.count += 1;
        summary.total += amount;
        summary.byGroup[group] = (summary.byGroup[group] || 0) + amount;
        if (countsInIncomeStatement(flow)) summary.incomeStatement += amount;
        else summary.outsideStatement += amount;
        if (countsInCashCount(flow)) summary.fromCash += amount;
        if (flow.pendingConfirmation) summary.estimated += amount;
    });
    summary.total = cents(summary.total);
    summary.incomeStatement = cents(summary.incomeStatement);
    summary.outsideStatement = cents(summary.outsideStatement);
    summary.fromCash = cents(summary.fromCash);
    summary.estimated = cents(summary.estimated);
    Object.keys(summary.byGroup).forEach((group) => (summary.byGroup[group] = cents(summary.byGroup[group])));
    return summary;
};

// ---------------------------------------------------------------------------
// Editar un gasto (§4.1: gerente el mismo día y en su sucursal; admin de sucursal
// sin límite en la suya; admin todos). Si la caja del gasto ya cerró, su corte
// guardó el esperado: **el monto y "salió de la caja" no se editan** — se cancela y
// se captura de nuevo. Descripción y categoría sí se corrigen.
// ---------------------------------------------------------------------------

/** El encabezado que `describeExpense` antepone (nómina, insumo), para recuperar la nota. */
const headOf = (flow: CashFlow): string => {
    if (flow.payrollUserName) return `Nómina: ${flow.payrollUserName}`;
    if (flow.inputName) return [flow.inputName, describePurchaseQuantity(flow)].filter(Boolean).join(' — ');
    return '';
};

/** La nota que escribió quien capturó. Lo de antes de F3 no la guardaba aparte: se deduce. */
export const noteOf = (flow: CashFlow): string => {
    if (flow.note !== undefined) return flow.note;
    const description = (flow.description || '').trim();
    const head = headOf(flow);
    if (!head) return description;
    if (description === head) return '';
    return description.startsWith(`${head} — `) ? description.slice(head.length + 3) : description;
};

/** El borrador para editar un gasto con el mismo formulario con el que se capturó. */
export const draftFromCashFlow = (flow: CashFlow, categories: ExpenseCategory[]): ExpenseDraft => {
    const categoryKey = expenseCategoryKeyOf(flow);
    return {
        category: categories.find((category) => category.id === categoryKey),
        amount: Number(flow.amount) || 0,
        note: noteOf(flow),
        paidFromCash: flow.pendingConfirmation ? !!flow.paidFromCashOnConfirm : flow.paidFromCash !== false,
        ...(flow.payrollUserId ? { payrollUser: { uid: flow.payrollUserId, name: flow.payrollUserName || '' } } : {}),
        ...(flow.inputId ? { input: { id: flow.inputId, name: flow.inputName || '' } } : {}),
        ...(Number(flow.purchaseQuantity) > 0
            ? {
                  purchaseQuantity: Number(flow.purchaseQuantity),
                  purchaseUnitMode: flow.purchaseUnitMode === 'base' ? 'base' : 'presentation',
                  ...(flow.purchaseUnitLabel ? { purchaseUnitLabel: flow.purchaseUnitLabel } : {})
              }
            : {}),
        ...(flow.period ? { period: flow.period } : {})
    };
};

/** ¿La edición cambia lo que el gasto le hace a la caja? (monto o "salió de la caja") */
export const cashEffectChanged = (flow: CashFlow, draft: ExpenseDraft): boolean =>
    cents(Number(draft.amount) || 0) !== cents(Number(flow.amount) || 0) ||
    draft.paidFromCash !== (flow.paidFromCash !== false);

export interface ExpenseEditContext {
    subject: PermissionSubject;
    /** La caja en cuya ventana cae el gasto ya cerró (su corte guardó el esperado). */
    amountLocked: boolean;
    now?: Date;
}

export const validateExpenseEdit = (flow: CashFlow, draft: ExpenseDraft, context: ExpenseEditContext): string | null => {
    if (!isExpenseFlow(flow)) return 'Solo se editan gastos';
    if (!isActiveFlow(flow)) return 'Este gasto está cancelado: ya no se edita';
    const date = toDateSafe(flow.date);
    if (!can(context.subject, Capability.ExpenseEdit, { branchOfficeId: flow.branchOfficeId, date, now: context.now })) {
        return getCapabilityRule(context.subject, Capability.ExpenseEdit) === 'ownBranchesSameDay'
            ? 'Solo puedes editar gastos de tu sucursal y del mismo día'
            : 'Tu perfil no puede editar este gasto';
    }
    if (flow.pendingConfirmation) return 'Este gasto está por confirmar: confírmalo desde "Gastos fijos"';
    const touchesCash = (flow.paidFromCash !== false || draft.paidFromCash) && cashEffectChanged(flow, draft);
    if (context.amountLocked && touchesCash) {
        return 'La caja de este gasto ya cerró: el monto no se cambia. Cancélalo y captúralo de nuevo';
    }
    // Las reglas de la categoría son las de capturar, en la sucursal del gasto. La caja
    // abierta no se pide: editar no saca dinero hoy (lo que ya cerró se revisó arriba).
    return validateExpense(draft, { subject: context.subject, branchOfficeId: flow.branchOfficeId, hasOpenCashRegister: true });
};

/** Los campos opcionales que una edición puede dejar de tener (p. ej. de Nómina a Limpieza). */
const OPTIONAL_EXPENSE_FIELDS: (keyof CashFlow)[] = [
    'payrollUserId',
    'payrollUserName',
    'inputId',
    'inputName',
    'purchaseQuantity',
    'purchaseUnitMode',
    'purchaseUnitLabel',
    'period',
    'note'
];

/**
 * Qué se escribe al editar: lo que arma el mismo constructor de la captura, y qué se
 * borra. Si el gasto HABÍA entrado al inventario y deja de ser compra de insumo (o cambia
 * de insumo o de cantidad), queda `inventorySyncPending` para que el server iguale el
 * ledger (lo que entró, sale).
 */
export const buildExpenseEdit = (flow: CashFlow, draft: ExpenseDraft): { changes: NewCashFlow; removed: (keyof CashFlow)[] } => {
    const built = buildExpenseCashFlow(draft);
    const changes: NewCashFlow =
        !built.inventorySyncPending && flow.purchaseQuantity !== undefined ? { ...built, inventorySyncPending: true } : built;
    const removed = OPTIONAL_EXPENSE_FIELDS.filter(
        (field) => flow[field] !== undefined && (changes as Partial<CashFlow>)[field] === undefined
    );
    return { changes, removed };
};

// ---------------------------------------------------------------------------
// Cancelar (vista 6): motivo obligatorio y el PIN de quien cancela. **Nunca se
// borra**: queda a la vista, tachado, y deja de contar en el corte, la lista y el ER.
// ---------------------------------------------------------------------------

export const validateExpenseCancel = (flow: CashFlow, reason: string, subject: PermissionSubject): string | null => {
    if (!isExpenseFlow(flow)) return 'Solo se cancelan gastos';
    if (!isActiveFlow(flow)) return 'Este gasto ya estaba cancelado';
    if (!can(subject, Capability.ExpenseCancel, { branchOfficeId: flow.branchOfficeId })) {
        return 'Tu perfil no puede cancelar gastos de esta sucursal';
    }
    if (reason.trim().length < 3) return 'Escribe el motivo de la cancelación';
    return null;
};

// ---------------------------------------------------------------------------
// Gastos fijos (§3.4, F4). Registrar el primer gasto de un mes (renta, luz) puede
// volverlo plantilla: el aseo genera los meses siguientes. Crear la plantilla es
// "capturar gasto fijo" (gerente para arriba); pausarla, eliminarla y confirmar
// los estimados, del admin de sucursal para arriba (§8.5).
// ---------------------------------------------------------------------------

/** ¿Puede volver plantilla este gasto? Solo fijos y variables, y quien captura fijos. */
export const canMakeRecurring = (category: ExpenseCategory | undefined, subject: PermissionSubject, branchOfficeId?: string): boolean =>
    !!category && isMonthlyGroup(category.group) && can(subject, Capability.ExpenseCaptureFixed, { branchOfficeId });

/** La renta es del mismo monto; la luz cambia cada mes. Es solo la opción sugerida. */
export const defaultAmountMode = (category?: ExpenseCategory): RecurringAmountMode =>
    category?.group === ExpenseGroup.Fixed ? 'fixed' : 'variable';

/** La plantilla que nace con el primer gasto. **Sin `undefined`.** */
export const buildRecurringExpense = (
    draft: ExpenseDraft,
    stamp: { id: string; mode: RecurringAmountMode; branchOfficeId: string; author: { uid: string; name: string }; now: Date }
): RecurringExpense => {
    const category = draft.category!;
    return {
        id: stamp.id,
        branchOfficeId: stamp.branchOfficeId,
        categoryId: category.id,
        categoryName: category.name,
        categoryGroup: category.group,
        description: describeExpense(draft) || category.name,
        amount: cents(Number(draft.amount) || 0),
        amountMode: stamp.mode,
        paidFromCash: draft.paidFromCash,
        startPeriod: draft.period!,
        lastPeriod: draft.period!,
        status: 'active',
        createdBy: stamp.author.uid,
        ...(stamp.author.name ? { createdByName: stamp.author.name } : {}),
        createdAt: stamp.now
    };
};

export const validateRecurringManage = (template: Pick<RecurringExpense, 'branchOfficeId'>, subject: PermissionSubject): string | null =>
    can(subject, Capability.RecurringExpenseManage, { branchOfficeId: template.branchOfficeId })
        ? null
        : 'Tu perfil no puede administrar los gastos fijos de esta sucursal';

export interface ConfirmInstanceContext {
    subject: PermissionSubject;
    /** Caja abierta de hoy en la sucursal del gasto. */
    hasOpenCashRegister: boolean;
}

/** Confirmar un estimado: el monto del recibo. Si sale de la caja, resta en la caja abierta. */
export const validateConfirmInstance = (
    flow: CashFlow,
    amount: number,
    paidFromCash: boolean,
    context: ConfirmInstanceContext
): string | null => {
    if (!isActiveFlow(flow)) return 'Este gasto está cancelado';
    if (!flow.pendingConfirmation) return 'Este gasto ya estaba confirmado';
    if (!can(context.subject, Capability.RecurringExpenseManage, { branchOfficeId: flow.branchOfficeId })) {
        return 'Confirma el monto el admin de la sucursal o el admin general';
    }
    if (!(Number(amount) > 0)) return 'El monto tiene que ser mayor a cero';
    if (paidFromCash && !context.hasOpenCashRegister) {
        return 'Para sacarlo de la caja tiene que haber una caja abierta en esa sucursal (entra al POS de la sucursal)';
    }
    return null;
};

/**
 * Lo que se escribe al confirmar. Si sale de la caja, la fecha pasa al instante de
 * confirmar para que caiga en la ventana de la caja abierta (el corte reparte por
 * ventana). El mes (`period`) no cambia.
 */
export const buildInstanceConfirmation = (
    amount: number,
    paidFromCash: boolean,
    author: { uid: string; name: string },
    now: Date
): Partial<CashFlow> => ({
    amount: cents(Number(amount) || 0),
    paidFromCash,
    pendingConfirmation: false,
    confirmedAt: now,
    confirmedBy: author.uid,
    ...(author.name ? { confirmedByName: author.name } : {}),
    ...(paidFromCash ? { date: now } : {})
});
